import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faUserPlus, faSpinner, faBuilding, faGlobe, faTag, faMapMarkerAlt, faReceipt
} from '@fortawesome/free-solid-svg-icons';
import { crmService } from '../../services/crmService';
import { COUNTRY_OPTIONS } from '../../utils/constants';
import { getErrorMessage } from '../../utils/errorMessages';

const PARTNER_TYPE_OPTIONS = [
    'Seller (Creditor)',
    'Buyer (Debtor)',
    'Consumer',
    'Producer',
    'Service provider',
    'End User',
    'Ship Owners',
    'Charterer',
    'Brokers',
    'Governmental',
    'Non-governmental',
    'Parastatal',
];

/* The country box lists names; the reading gives an ISO code (CH, DE). */
export const countryOptionFor = (isoCode) => {
    if (!isoCode) return '';
    let name = '';
    try {
        name = new Intl.DisplayNames(['en'], { type: 'region' }).of(String(isoCode).toUpperCase()) || '';
    } catch (e) {
        return '';
    }
    // English region names that differ from the country box's own labels:
    // accents, "St." and "&" are evened out, the rest is named here.
    const plain = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        .replace(/^st\. /, 'saint ').replace(/ & /g, ' and ').replace(/’/g, "'");
    const ALIASES = {
        'turkiye': 'turkey', 'czechia': 'czech republic', 'hong kong sar china': 'hong kong',
        "cote d'ivoire": 'ivory coast', 'congo - brazzaville': 'congo',
        'congo - kinshasa': 'democratic republic of the congo', 'myanmar (burma)': 'myanmar',
        'saint vincent and grenadines': 'saint vincent and the grenadines', 'timor-leste': 'east timor',
        'palestinian territories': 'palestine',
    };
    const wanted = plain(name);
    const label = ALIASES[wanted] || wanted;
    const match = COUNTRY_OPTIONS.find(c => plain(c.label) === label);
    return match ? match.value : '';
};

/**
 * Add the supplier of a captured invoice to CRM with what its PDF printed.
 *
 * Gritt (2026-10-19): the partner arrived with no address and nothing said it
 * still needed completing. Now the read address, country and VAT number are
 * filled in (and can be corrected), the partner is saved through CRM's
 * quick-add as "added from a captured invoice", and it carries a "details to
 * complete" tag plus a reminder in the adder's bell (crm quick-create-profile).
 */
const InvoiceCrmQuickCreate = ({ supplierName, extracted = {}, onPartnerCreated, onBack }) => {
    const [formData, setFormData] = useState({
        legalEntityName: supplierName || '',
        tradingShortName: '',
        country: countryOptionFor(extracted.supplier_country_code),
        address: extracted.supplier_address || '',
        vatNumber: extracted.supplier_vat_number || '',
        partnerType: 'Seller (Creditor)',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.legalEntityName.trim()) {
            setError('Legal Entity Name is required.');
            return;
        }

        setIsSubmitting(true);
        try {
            const countryLabel = COUNTRY_OPTIONS.find(c => c.value === formData.country)?.label || '';
            const newProfile = await crmService.quickCreateProfile({
                legal_entity_name: formData.legalEntityName,
                trading_short_name: formData.tradingShortName,
                partner_type: formData.partnerType,
                country: countryLabel,
                address: formData.address,
                vat_number: formData.vatNumber,
                source: 'invoice_capture',
            });

            onPartnerCreated({
                id: newProfile.id,
                legal_entity_name: newProfile.legal_entity_name,
                trading_short_name: newProfile.trading_short_name,
                details_to_complete: !!newProfile.details_to_complete,
            });
        } catch (err) {
            if (err?.isDuplicate && err.existingProfile) {
                // Already in CRM (perhaps still waiting for approval): use it.
                onPartnerCreated({ ...err.existingProfile, already_in_crm: true });
                return;
            }
            console.error('Failed to create CRM partner:', err);
            setError(getErrorMessage(err, 'Failed to create partner. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-body" style={{ padding: '1.5rem' }}>
            <div style={{
                backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '0.5rem',
                padding: '1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem'
            }}>
                <FontAwesomeIcon icon={faUserPlus} style={{ color: '#d97706', marginTop: '2px', fontSize: '1.1rem' }} />
                <div>
                    <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '0.25rem' }}>
                        Supplier not found in CRM
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#78350f' }}>
                        The extracted supplier name "<strong>{supplierName}</strong>" did not match any approved CRM partner.
                        The details below were read from the invoice; correct them if needed. The new partner is
                        marked "details to complete" and you get a reminder in your bell to finish it.
                    </div>
                </div>
            </div>

            {error && (
                <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>
            )}

            <form className="hms-stacked-form" onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Legal Entity Name */}
                    <div className="form-field">
                        <label className="field-label required" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                            <FontAwesomeIcon icon={faBuilding} /> Legal Entity Name
                        </label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.legalEntityName}
                            onChange={(e) => setFormData(prev => ({ ...prev, legalEntityName: e.target.value }))}
                            placeholder="Official legal name of the company"
                            required
                            style={{ padding: '0.65rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', width: '100%' }}
                        />
                    </div>

                    {/* Trading Short Name */}
                    <div className="form-field">
                        <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                            <FontAwesomeIcon icon={faTag} /> Trading Short Name
                        </label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.tradingShortName}
                            onChange={(e) => setFormData(prev => ({ ...prev, tradingShortName: e.target.value }))}
                            placeholder="e.g. trading name or abbreviation (optional)"
                            style={{ padding: '0.65rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', width: '100%' }}
                        />
                    </div>

                    {/* Country */}
                    <div className="form-field">
                        <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                            <FontAwesomeIcon icon={faGlobe} /> Country
                        </label>
                        <select
                            className="form-select"
                            value={formData.country}
                            onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                            style={{ padding: '0.65rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', width: '100%' }}
                        >
                            <option value="">Select country (optional)</option>
                            {COUNTRY_OPTIONS.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Address as printed on the invoice */}
                    <div className="form-field">
                        <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} /> Address
                        </label>
                        <textarea
                            className="form-input"
                            rows={2}
                            value={formData.address}
                            onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                            placeholder="Street, postcode, city, country"
                            style={{ padding: '0.65rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', width: '100%' }}
                        />
                    </div>

                    {/* VAT number as printed on the invoice */}
                    <div className="form-field">
                        <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                            <FontAwesomeIcon icon={faReceipt} /> VAT Number
                        </label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.vatNumber}
                            onChange={(e) => setFormData(prev => ({ ...prev, vatNumber: e.target.value }))}
                            placeholder="e.g. CHE-123.456.789 MWST"
                            style={{ padding: '0.65rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', width: '100%' }}
                        />
                    </div>

                    {/* Partner Type */}
                    <div className="form-field">
                        <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                            <FontAwesomeIcon icon={faUserPlus} /> Partner Type
                        </label>
                        <select
                            className="form-select"
                            value={formData.partnerType}
                            onChange={(e) => setFormData(prev => ({ ...prev, partnerType: e.target.value }))}
                            style={{ padding: '0.65rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', width: '100%' }}
                        >
                            {PARTNER_TYPE_OPTIONS.map(pt => (
                                <option key={pt} value={pt}>{pt}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div style={{
                    display: 'flex', gap: '0.75rem', justifyContent: 'center',
                    marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9'
                }}>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onBack}
                        disabled={isSubmitting}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '120px',
                            justifyContent: 'center', padding: '0.75rem 1rem', backgroundColor: '#ffffff',
                            color: '#475569', border: '1px solid #e2e8f0', borderRadius: '0.375rem',
                            fontSize: '0.875rem', fontWeight: 500, cursor: isSubmitting ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} /> Back to Results
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting || !formData.legalEntityName.trim()}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '160px',
                            justifyContent: 'center', padding: '0.75rem 1rem',
                            background: (!isSubmitting && formData.legalEntityName.trim())
                                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                : '#94a3b8',
                            color: 'white', border: 'none', borderRadius: '0.375rem',
                            fontSize: '0.875rem', fontWeight: 500,
                            cursor: (isSubmitting || !formData.legalEntityName.trim()) ? 'not-allowed' : 'pointer',
                            boxShadow: (!isSubmitting && formData.legalEntityName.trim()) ? '0 4px 12px rgba(59, 130, 246, 0.4)' : 'none'
                        }}
                    >
                        {isSubmitting ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /> Creating...</>
                        ) : (
                            <><FontAwesomeIcon icon={faUserPlus} /> Create Partner</>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default InvoiceCrmQuickCreate;
